import React, { useMemo } from "react";
import loadableVisibility from "react-loadable-visibility/loadable-components";

import DocDownloadSkeleton from "./DocDownload/Skeleton";
import { DocDownloadProps } from "./DocDownload";
import { DegreesBannerMediumProps } from "components/SixtyThreeDegreesBanner/Medium";
import SixtyThreeDegreesBannerMediumSkeleton from "./SixtyThreeDegreesBanner/Medium/Skeleton";
import { TestimonialsProps } from "components/Testimonials";
import TestimonialsSkeleton from "./Testimonials/Skeleton";
import { RelatedContentProps } from "components/RelatedContent";
import { VideoWithCaptionProps } from "components/VideoWithCaption";
import { TwoColumnImageProps } from "./TwoColumnImage/types";
import TextWithSubheading, { TextWithSubheadingProps } from "components/TextWithSubheading";
import { TableSectionProps } from "components/TableSection";
import TableSectionSkeleton from "./TableSection/Skeleton";
import { StatsProps } from "components/Stats";
import StatsSkeleton from "./Stats/Skeleton";
import { DegreesBannerSmallProps } from "components/SixtyThreeDegreesBanner/Small";
import { DegreesBannerLargeProps } from "components/SixtyThreeDegreesBanner/Large";
import { ImageWithCaptionProps } from "components/ImageWithCaption";
import { FullwidthImageAndTextProps } from "components/FullwidthImageAndText";
import { FindATrainerProps } from "components/FindATrainer";
import { AccordionProps } from "components/Accordion";
import ContentsHeading, { ContentsHeadingProps } from "components/ContentsHeading";
import SixtyThreeDegreesBannerSmallSkeleton from "./SixtyThreeDegreesBanner/Small/Skeleton";
import SixtyThreeDegreesBannerLargeSkeleton from "./SixtyThreeDegreesBanner/Large/Skeleton";
import AccordionSkeleton from "./Accordion/Skeleton";
import TwoColumnImageSkeleton from "./TwoColumnImage/Skeleton";

type PostRenderComponentsProps = {
  components: any;
  noContainer?: boolean;
};

const PostRenderComponents: React.FC<PostRenderComponentsProps> = ({ components, noContainer }) => {
  let counter = 1;
  return (
    <>
      {components?.map((component: any, index: any) => {
        if (component["contents_heading"]) {
          return (
            <ContentsHeading
              {...(component.contents_heading as ContentsHeadingProps)}
              id={counter++}
              key={index}
              noContainer={noContainer}
            />
          );
        }
        if (component["text"]) {
          return (
            <TextWithSubheading
              {...(component.text as TextWithSubheadingProps)}
              key={index}
              noContainer={noContainer}
            />
          );
        }
        if (component["text_with_subheading"]) {
          return (
            <TextWithSubheading
              {...(component.text_with_subheading as TextWithSubheadingProps)}
              key={index}
              noContainer={noContainer}
            />
          );
        }
        if (component["document_download"]) {
          const DocDownload = useMemo(
            () =>
              loadableVisibility(() => import("components/DocDownload"), {
                fallback: <DocDownloadSkeleton {...(component.document_download as DocDownloadProps)} />,
              }),
            [component.document_download]
          );
          return <DocDownload {...(component.document_download as DocDownloadProps)} key={index} lazyload />;
        }
        if (component["documents_download"]) {
          const DocDownload = useMemo(
            () =>
              loadableVisibility(() => import("components/DocDownload"), {
                fallback: <DocDownloadSkeleton {...(component.documents_download as DocDownloadProps)} />,
              }),
            [component.documents_download]
          );
          return <DocDownload {...(component.documents_download as DocDownloadProps)} key={index} lazyload />;
        }
        if (component["accordion"]) {
          const Accordion = useMemo(
            () =>
              loadableVisibility(() => import("components/Accordion"), {
                fallback: <AccordionSkeleton {...(component.accordion as AccordionProps)} />,
              }),
            [component.accordion]
          );
          return <Accordion {...(component.accordion as AccordionProps)} key={index} lazyload />;
        }
        if (component["findATrainer"]) {
          const FindATrainer = useMemo(
            () => loadableVisibility(() => import("components/FindATrainer")),
            [component.findATrainer]
          );
          return <FindATrainer {...(component.findATrainer as FindATrainerProps)} key={index} lazyload />;
        }
        if (component["fullwidth_image_and_text"]) {
          const FullwidthImageAndText = useMemo(
            () => loadableVisibility(() => import("components/FullwidthImageAndText")),
            [component.fullwidth_image_and_text]
          );
          return (
            <FullwidthImageAndText
              {...(component.fullwidth_image_and_text as FullwidthImageAndTextProps)}
              key={index}
              lazyload
            />
          );
        }
        if (component["image_with_caption"]) {
          const ImageWithCaption = useMemo(
            () => loadableVisibility(() => import("components/ImageWithCaption")),
            [component.image_with_caption]
          );
          return (
            <ImageWithCaption
              {...(component.image_with_caption as ImageWithCaptionProps)}
              key={index}
              noContainer={noContainer}
              lazyload
            />
          );
        }

        if (component["large_degrees_banner"]) {
          const SixtyThreeDegreesBannerLarge = useMemo(
            () =>
              loadableVisibility(() => import("components/SixtyThreeDegreesBanner/Large"), {
                fallback: (
                  <SixtyThreeDegreesBannerLargeSkeleton
                    {...(component.large_degrees_banner as DegreesBannerLargeProps)}
                  />
                ),
              }),
            [component.large_degrees_banner]
          );
          return (
            <SixtyThreeDegreesBannerLarge
              {...(component.large_degrees_banner as DegreesBannerLargeProps)}
              key={index}
              lazyload
            />
          );
        }
        if (component["medium_degrees_banner"]) {
          const SixtyThreeDegreesBannerMedium = useMemo(
            () =>
              loadableVisibility(() => import("components/SixtyThreeDegreesBanner/Medium"), {
                fallback: (
                  <SixtyThreeDegreesBannerMediumSkeleton
                    {...(component.medium_degrees_banner as DegreesBannerMediumProps)}
                  />
                ),
              }),
            [component.medium_degrees_banner]
          );
          return (
            <SixtyThreeDegreesBannerMedium
              {...(component.medium_degrees_banner as DegreesBannerMediumProps)}
              key={index}
              lazyload
            />
          );
        }
        if (component["small_degrees_banner"]) {
          const SixtyThreeDegreesBannerSmall = useMemo(
            () =>
              loadableVisibility(() => import("components/SixtyThreeDegreesBanner/Small"), {
                fallback: (
                  <SixtyThreeDegreesBannerSmallSkeleton
                    {...(component.small_degrees_banner as DegreesBannerSmallProps)}
                  />
                ),
              }),
            [component.small_degrees_banner]
          );
          return (
            <SixtyThreeDegreesBannerSmall
              {...(component.small_degrees_banner as DegreesBannerSmallProps)}
              key={index}
              lazyload
            />
          );
        }

        if (component["related_content"]) {
          const RelatedContent = useMemo(
            () => loadableVisibility(() => import("components/RelatedContent")),
            [component.related_content]
          );
          return <RelatedContent {...(component.related_content as RelatedContentProps)} key={index} lazyload />;
        }
        if (component["stats"]) {
          const Stats = useMemo(
            () =>
              loadableVisibility(() => import("components/Stats"), {
                fallback: <StatsSkeleton {...(component.stats as StatsProps)} />,
              }),
            [component.stats]
          );
          return <Stats {...(component.stats as StatsProps)} key={index} lazyload />;
        }
        if (component["table_section"]) {
          const TableSection = useMemo(
            () =>
              loadableVisibility(() => import("components/TableSection"), {
                fallback: <TableSectionSkeleton {...(component.table_section as TableSectionProps)} />,
              }),
            [component.table_section]
          );
          return (
            <TableSection
              {...(component.table_section as TableSectionProps)}
              key={index}
              noContainer={noContainer}
              lazyload
            />
          );
        }
        if (component["testimonials_section"]) {
          const Testimonials = useMemo(
            () =>
              loadableVisibility(() => import("components/Testimonials"), {
                fallback: <TestimonialsSkeleton {...(component.testimonials_section as TestimonialsProps)} />,
              }),
            [component.testimonials_section]
          );
          return <Testimonials {...(component.testimonials_section as TestimonialsProps)} key={index} lazyload />;
        }
        if (component["twoColumnImage"]) {
          const TwoColumnImage = useMemo(
            () =>
              loadableVisibility(() => import("components/TwoColumnImage"), {
                fallback: <TwoColumnImageSkeleton {...(component.twoColumnImage as TwoColumnImageProps)} />,
              }),
            [component.twoColumnImage]
          );
          return <TwoColumnImage {...(component.twoColumnImage as TwoColumnImageProps)} key={index} lazyload />;
        }
        if (component["video_with_caption"]) {
          const VideoWithCaption = useMemo(
            () => loadableVisibility(() => import("components/VideoWithCaption")),
            [component.video_with_caption]
          );
          return (
            <VideoWithCaption
              {...(component.video_with_caption as VideoWithCaptionProps)}
              key={index}
              noContainer={noContainer}
              lazyload
            />
          );
        }
      })}
    </>
  );
};
export default React.memo(PostRenderComponents);
